import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut, type User } from 'firebase/auth';
import { get, ref } from 'firebase/database';
import { Loader2, Pencil } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { auth, database } from '../services/firebase';

type OrganizationRecord = {
  id: string;
  name: string;
  type?: string | null;
  stripeId?: string | null;
  subscriptionId?: string | null;
  isEnterprise?: boolean;
  createdAt?: number;
  createdBy?: string;
};

type UserProfile = {
  displayName?: string;
  needsOrganizationSetup?: boolean;
  organizationId?: string;
  organizationMembershipStatus?: string;
};

function formatTimestamp(value?: number) {
  if (!value) {
    return 'Unknown';
  }
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value));
  } catch {
    return new Date(value).toLocaleString();
  }
}

export default function Organization() {
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return localStorage.getItem('theme') === 'dark';
  });

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [organization, setOrganization] = useState<OrganizationRecord | null>(null);
  const [loadingOrganization, setLoadingOrganization] = useState(false);
  const [organizationError, setOrganizationError] = useState<string | null>(null);
  const [activeLocationsCount, setActiveLocationsCount] = useState(0);
  const [totalLocationsCount, setTotalLocationsCount] = useState(0);
  const [createdByName, setCreatedByName] = useState<string>('Unknown');
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, user => {
      setCurrentUser(user);
      setAuthChecked(true);
      if (!user) {
        navigate('/signin', {
          replace: true,
          state: { returnTo: '/organization' },
        });
      }
    });
    return unsubscribe;
  }, [navigate]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const storedTheme = localStorage.getItem('theme');
    if (storedTheme === 'dark') {
      setIsDarkMode(true);
    }
    if (storedTheme === 'light') {
      setIsDarkMode(false);
    }
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setProfile(null);
      setProfileLoading(false);
      return;
    }
    setProfileLoading(true);
    const profileRef = ref(database, `users/${currentUser.uid}`);
    void get(profileRef)
      .then(snapshot => {
        const value = snapshot.val() as UserProfile | null;
        if (value?.needsOrganizationSetup) {
          navigate('/organization/create', {
            replace: true,
            state: { returnTo: '/users' },
          });
          return;
        }
        setProfile(value);
      })
      .catch(error => {
        console.warn('Failed to load users profile data', error);
      })
      .finally(() => {
        setProfileLoading(false);
      });
  }, [currentUser, navigate]);

  useEffect(() => {
    if (!profile?.organizationId) {
      setOrganization(null);
      setActiveLocationsCount(0);
      setTotalLocationsCount(0);
      setLoadingOrganization(false);
      if (!profileLoading) {
        setOrganizationError(profile?.needsOrganizationSetup ? null : 'No organization details were found.');
      } else {
        setOrganizationError(null);
      }
      return;
    }

    const orgRef = ref(database, `organizations/${profile.organizationId}`);
    const locationsRef = ref(database, `organizationLocations/${profile.organizationId}`);
    let isActive = true;

    setLoadingOrganization(true);
    setOrganizationError(null);

    void (async () => {
      try {
        const [organizationSnapshot, locationsSnapshot] = await Promise.all([get(orgRef), get(locationsRef)]);
        if (!isActive) {
          return;
        }

        const organizationValue = organizationSnapshot.val() as (OrganizationRecord & { clinicName?: string }) | null;
        if (!organizationValue) {
          setOrganization(null);
          setOrganizationError('No organization details were found.');
          setActiveLocationsCount(0);
          setTotalLocationsCount(0);
          return;
        }

        setOrganization({
          id: profile.organizationId as string,
          name: organizationValue.name ?? organizationValue.clinicName ?? '',
          type: typeof organizationValue.type === 'string' ? organizationValue.type : null,
          stripeId: organizationValue.stripeId ?? null,
          subscriptionId: organizationValue.subscriptionId ?? null,
          isEnterprise: Boolean(organizationValue.isEnterprise),
          createdAt: organizationValue.createdAt,
          createdBy: organizationValue.createdBy,
        });

        const locationsValue = locationsSnapshot.val() as Record<string, { status?: string } | null> | null;
        if (!locationsValue) {
          setActiveLocationsCount(0);
          setTotalLocationsCount(0);
          return;
        }

        const totalLocations = Object.keys(locationsValue).length;
        const activeLocations = Object.values(locationsValue).reduce((count, locationData) => {
          const normalizedStatus =
            typeof locationData?.status === 'string'
              ? locationData.status.toLowerCase()
              : 'active';
          return normalizedStatus === 'active' ? count + 1 : count;
        }, 0);

        setTotalLocationsCount(totalLocations);
        setActiveLocationsCount(activeLocations);
      } catch (error) {
        if (!isActive) {
          return;
        }
        console.warn('Failed to load organization details', error);
        setOrganization(null);
        setOrganizationError('We were unable to load your organization details. Please try again later.');
        setActiveLocationsCount(0);
        setTotalLocationsCount(0);
      } finally {
        if (isActive) {
          setLoadingOrganization(false);
        }
      }
    })();

    return () => {
      isActive = false;
    };
  }, [profile?.organizationId, profile?.needsOrganizationSetup, profileLoading]);

  useEffect(() => {
    const createdById = organization?.createdBy;
    if (!createdById) {
      setCreatedByName('Unknown');
      return;
    }

    setCreatedByName('Loading...');

    let isActive = true;

    void (async () => {
      try {
        const snapshot = await get(ref(database, `users/${createdById}`));
        if (!isActive) {
          return;
        }
        const value = snapshot.val() as UserProfile | null;
        const displayName = value?.displayName?.trim();
        setCreatedByName(displayName && displayName.length > 0 ? displayName : 'Unknown');
      } catch (error) {
        if (isActive) {
          console.warn('Failed to load creator profile', error);
          setCreatedByName('Unknown');
        }
      }
    })();

    return () => {
      isActive = false;
    };
  }, [organization?.createdBy]);

  const cardClass = `${isDarkMode ? 'bg-slate-900/60 text-slate-100 ring-slate-800' : 'bg-white text-slate-900 ring-slate-200'} rounded-lg p-8 ring-1`;
  const mutedTextClass = `${isDarkMode ? 'text-slate-300' : 'text-slate-500'}`;
  const primaryActionButtonClass = `inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold shadow-sm transition ${isDarkMode ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-blue-600 text-white hover:bg-blue-700'}`;
  const loaderTextClass = isDarkMode ? 'text-slate-200' : 'text-slate-600';
  const hasSubscription = Boolean(organization?.subscriptionId);
  const selectedPlan = (organization as { subscription?: { planId?: string } } | null)?.subscription?.planId ?? 'basic';
  const selectedPlanIsBasic = selectedPlan === 'basic';

  const handleThemeToggle = () => {
    setIsDarkMode(prev => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        localStorage.setItem('theme', next ? 'dark' : 'light');
      }
      return next;
    });
  };

  const handleSignIn = () => {
    navigate('/signin', { state: { returnTo: '/organization' } });
  };

  const handleCreateAccount = () => {
    navigate('/register', { state: { returnTo: '/organization' } });
  };

  const handleLogout = () => {
    void signOut(auth)
      .then(() => {
        navigate('/signin', {
          replace: true,
          state: { returnTo: '/organization' },
        });
      })
      .catch(error => {
        console.warn('Failed to sign out', error);
      });
  };

  const hasOrganizationAccess = Boolean(profile?.organizationId);
  const isContentLoading = profileLoading || (profile?.organizationId ? loadingOrganization : false);

  if (!authChecked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-900 text-slate-100">
        <div className="flex items-center gap-3 text-lg font-semibold">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          Fetching your organization...
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return null;
  }

  return (
    <DashboardLayout
      isDarkMode={isDarkMode}
      onToggleTheme={handleThemeToggle}
      currentUser={currentUser}
      onSignIn={handleSignIn}
      onCreateAccount={handleCreateAccount}
      onLogout={handleLogout}
      needsOrganizationSetup={profile?.needsOrganizationSetup}
      organizationMembershipStatus={profile?.organizationMembershipStatus}
      hasOrganizationAccess={hasOrganizationAccess}
    >
      {isContentLoading ? (
        <div className="flex justify-center pt-12">
          <div className={`flex items-center gap-3 text-sm font-semibold ${loaderTextClass}`}>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Loading organization details...
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <section className={cardClass}>
            {/* Header Row */}
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-bold">Organization overview</h1>

              <button
                type="button"
                className={primaryActionButtonClass}
              >
                <Pencil className="h-4 w-4" aria-hidden="true" />
                Edit Organization
              </button>
            </div>

            {/* Subtitle below both */}
            <div className="mt-3 mb-3">
              <p className={mutedTextClass}>
                View the information for your organization.
              </p>
            </div>

            <div
              className={`mt-8 space-y-4 rounded-xl border px-4 py-5 text-sm ${
                isDarkMode
                  ? 'border-slate-800 bg-slate-900/70 text-slate-200'
                  : 'border-slate-200 bg-slate-50 text-slate-700'
              }`}
            >
              <div>
                {organization?.type ? (
                  <p className={`mt-1 text-sm ${mutedTextClass}`}>{organization.type}</p>
                ) : null}
                <p className="mt-1 text-lg font-semibold">{organization?.name}</p>
              </div>
            </div>

            <div className="mt-10 space-y-6">
              {organization ? (
                <div className="space-y-6 text-sm">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Plan</p>
                      <p className="mt-1 text-base font-semibold">
                        {selectedPlanIsBasic ? 'Free' : 'Premium'}
                      </p>
                      <p className={`mt-1 text-xs ${mutedTextClass}`}>
                        {hasSubscription ? (
                          'Billing is active for this organization.'
                        ) : (
                          <>
                            Upgrade to <strong>Premium</strong> to appear in the app.
                          </>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Subscription status</p>
                      <p className="mt-1 text-base font-semibold">{hasSubscription ? 'Active' : 'None'}</p>
                      <p className={`mt-1 text-xs ${mutedTextClass}`}>
                        {hasSubscription
                          ? 'Stripe securely handles checkout and billing management.'
                          : 'Stripe securely handles billing once selected.'}
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-3">
                    <div className="sm:col-span-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Locations</p>
                      <div className="mt-3 grid gap-4 sm:grid-cols-3">
                        <div className={`${isDarkMode ? 'bg-slate-900/60' : 'bg-slate-50'} rounded-xl border border-dashed border-slate-200/60 p-4`}>
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Active</p>
                          <p className="mt-1 text-2xl font-bold">{activeLocationsCount}</p>
                        </div>
                        <div className={`${isDarkMode ? 'bg-slate-900/60' : 'bg-slate-50'} rounded-xl border border-dashed border-slate-200/60 p-4`}>
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Inactive</p>
                          <p className="mt-1 text-2xl font-bold">{Math.max(totalLocationsCount - activeLocationsCount, 0)}</p>
                        </div>
                        <div className={`${isDarkMode ? 'bg-slate-900/60' : 'bg-slate-50'} rounded-xl border border-dashed border-slate-200/60 p-4`}>
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Total</p>
                          <p className="mt-1 text-2xl font-bold">{totalLocationsCount}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Created by</p>
                      <p className="mt-1 text-base font-semibold">{createdByName}</p>
                      <p className={`mt-1 text-xs ${mutedTextClass}`}>
                        {formatTimestamp(organization.createdAt)}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-red-500">
                  {organizationError ?? 'No organization details were found.'}
                </div>
              )}
            </div>
          </section>
      </div>
      )}
    </DashboardLayout>
  );
}
